import React from 'react';
import { UserCheck, ChevronRight, Globe, MapPin, Share2, PhoneCall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import type { LeadItem } from './types';

interface RecentLeadsCardProps {
  leads: LeadItem[];
  onSelectLead: (lead: LeadItem) => void;
}

export const RecentLeadsCard: React.FC<RecentLeadsCardProps> = ({ leads, onSelectLead }) => {
  const navigate = useNavigate();

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'Website':
        return <Globe className="h-3 w-3 text-info-text" />;
      case 'Field Visit':
        return <MapPin className="h-3 w-3 text-success-text" />;
      case 'Referral':
        return <Share2 className="h-3 w-3 text-primary" />;
      default:
        return <PhoneCall className="h-3 w-3 text-on-surface-variant" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-info/10 text-info-text border-info/30';
      case 'contacted':
        return 'bg-primary-container text-primary-dim border-primary/30';
      case 'qualified':
        return 'bg-success/10 text-success-text border-success/30';
      case 'sampling':
        return 'bg-warning/10 text-warning-text border-warning/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-outline/80 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-info/10 text-info-text">
            <UserCheck className="h-4 w-4" />
          </div>
          <div>
            <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
              Recent Leads
            </span>
            <div className="text-data-xs text-on-surface-subtle">Latest enquiries from website & field</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate(ROUTES.pipeline)}
          className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline cursor-pointer"
        >
          <span>View All</span>
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Table Rows */}
      <div className="my-2 overflow-x-auto">
        <table className="w-full text-left text-body-sm">
          <thead>
            <tr className="border-b border-border text-data-xs font-semibold text-on-surface-subtle">
              <th className="pb-2 font-medium">NAME</th>
              <th className="pb-2 font-medium">COMPANY</th>
              <th className="pb-2 font-medium">SOURCE</th>
              <th className="pb-2 font-medium">DATE</th>
              <th className="pb-2 text-right font-medium">STATUS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {leads.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-body-sm text-on-surface-subtle">
                  No recent leads assigned to this territory yet.
                </td>
              </tr>
            ) : (
              leads.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={() => onSelectLead(lead)}
                  className="group cursor-pointer transition hover:bg-surface-variant"
                >
                  <td className="py-2.5 pr-2 font-semibold text-on-surface group-hover:text-primary">
                    {lead.name}
                  </td>
                  <td className="py-2.5 pr-2 text-on-surface-subtle">{lead.company}</td>
                  <td className="py-2.5 pr-2">
                    <div className="inline-flex items-center gap-1 text-data-xs text-on-surface-variant">
                      {getSourceIcon(lead.source)}
                      <span>{lead.source}</span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-2 text-data-xs text-on-surface-subtle whitespace-nowrap">
                    {lead.createdDate}
                  </td>
                  <td className="font-mono py-2.5 text-right tabular-nums text-data-sm">
                    <span
                      className={`inline-block rounded-md border px-2 py-0.5 text-data-xs font-semibold capitalize ${getStatusBadge(
                        lead.status
                      )}`}
                    >
                      {lead.statusLabel}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Quick Tip */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Assigned territory leads:</span>
        <span className="font-semibold text-on-surface">{leads.length} active prospects</span>
      </div>
    </div>
  );
};
